import { BoxType } from '@lite-workspace/dto-fotoclass-templates';
import type { CustomObjectTemplateBox } from '@lite-workspace/dto-fotoclass-templates';
import type { DrawableObjectData } from '../../interfaces/drawable-data.interface';
import {
  buildObjectBakedStyleMeta,
  buildObjectPassthroughMeta,
  carriedBoxFields,
  mergeBoxPassthroughMeta,
  objectBakedStyleParams,
  objectPassthroughParams,
} from '../passthrough';
import { buildDrawableEffects, mergeDrawableEffects } from './effects';
import { rectFromBox, rectToBox } from './rect';
import { mergeRotation } from './rotation';
import type { BoxDecoder, BoxEncoder } from './codec-contract';

export const decode: BoxDecoder<CustomObjectTemplateBox, DrawableObjectData> = (box, ctx) => {
  if (box.params.pixelData !== undefined) {
    ctx.drop('pixelData removed in Phase 3 — persisted asset lives in versions/url');
  }

  if (box.params.userApplied !== undefined) {
    ctx.warn('userApplied has no persisted home on DrawableObjectData — dropped');
  }

  const hasData = box.params.url !== undefined || box.params.fileId !== undefined || box.params.versions !== undefined;

  return {
    id: box.id,
    type: 'object',
    ...carriedBoxFields(box),
    order: 0, // dispatcher проставляет реальный order по индексу в collage
    canResize: false,
    opacity: box.opacity,
    blendMode: box.blendMode,
    visible: box.visible,
    locked: box.locked,
    rotation: box.rotation,
    effects: buildDrawableEffects(box.effects),
    data: hasData ? { url: box.params.url, fileId: box.params.fileId, versions: box.params.versions } : undefined,
    maskDataUrl: box.params.maskDataUrl,
    maskRect: box.params.maskRect,
    maskUrl: box.params.maskUrl,
    maskFileId: box.params.maskFileId,
    maskBaseBoxId: box.params.maskBaseBoxId,
    naturalAligns: {
      container: rectFromBox(box.rect, ctx),
      content: box.contentRect ? rectFromBox(box.contentRect, ctx.child('contentRect')) : undefined,
    },
    templateMeta: buildObjectBakedStyleMeta(buildObjectPassthroughMeta(box, box.params), box.params),
  };
};

export const encode: BoxEncoder<DrawableObjectData, CustomObjectTemplateBox> = (drawable, ctx) => {
  const box: CustomObjectTemplateBox = {
    type: BoxType.OBJECT,
    id: drawable.id,
    ...carriedBoxFields(drawable),
    rect: rectToBox(drawable.naturalAligns.container, ctx),
    contentRect: drawable.naturalAligns.content ? rectToBox(drawable.naturalAligns.content, ctx) : undefined,
    rotation: mergeRotation(drawable, ctx),
    opacity: drawable.opacity,
    blendMode: drawable.blendMode,
    visible: drawable.visible,
    locked: drawable.locked,
    effects: mergeDrawableEffects(
      drawable.effects,
      drawable.templateMeta?.rawEffects,
      drawable.layerStyleRendering === 'live' ? 'typed' : undefined,
    ),
    params: {
      url: drawable.data?.url,
      fileId: drawable.data?.fileId,
      versions: drawable.data?.versions,
      maskDataUrl: drawable.maskDataUrl,
      maskRect: drawable.maskRect,
      maskUrl: drawable.maskUrl,
      maskFileId: drawable.maskFileId,
      maskBaseBoxId: drawable.maskBaseBoxId,
      ...objectPassthroughParams(drawable.templateMeta),
      ...objectBakedStyleParams(drawable.templateMeta),
    },
  };
  return mergeBoxPassthroughMeta(box, drawable.templateMeta);
};
